import { useState, useCallback, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import Cookies from "js-cookie";
import { isAuthenticated } from "../lib/auth";
import {
  ARR_PARTNERSHIP_OBJ,
  LOGIN_PEOPLE_DETAIL,
  LOGIN_USER_PEOPLE,
} from "../config/constants";
import { getClassTypeName } from "../config/typecheck";
import { getClientTag, getutmId, getCampaignId } from "../utils/checkurl";
import { useMutation } from "@apollo/client";
import {
  CREATE_SESSION,
  CREATE_USER_ACTION,
} from "../queries/flymyasessionquery";
import moment from "moment";
import { getCookie } from "../utils/getcookies";
import { isJson } from "../utils/CheckIsJson";
import {
  openDB,
  addItem,
  getItems,
  //updateItem,
  //deleteItem,
  //clearObjectStore,
  //deleteDatabase,
} from "../lib/indexedDB";
//import { saveApiLog } from "../apolloClient";
import { getStorageValue } from "../utils/getMiniAppStorageData";
import { getKbzUserFields } from "../utils/flightEventTracker";
import clientV3 from "../apolloClient";
import { GET_LOGIN_PEOPLE_DETAILS } from "../queries/loginquery";
//import { saveApiLog } from "../apolloClient";

const localStorageName = "guest_flymya_session";
const PARTNERSHIP_RELOAD_FLAG = "partnership_sync_reloaded";
const deviceIdsName = "device_ids";

// Module scope, so these are shared across every component holding the hook -
// a per-hook ref would not help, since each caller gets its own instance.
// getLoginPeopleDetail is a page-load consistency check, and callers invoke it
// from mount effects; a caller that re-fires it (a mis-specified effect, a
// remount, several pages mounting at once) must not turn that into a burst of
// concurrent network-only queries against /graphql/customer.
let loginPeopleDetailInFlight = null;
let loginPeopleDetailLastFetchedAt = 0;
const LOGIN_PEOPLE_DETAIL_COOLDOWN_MS = 60 * 1000;

function FlymyaSessionHook() {
  const [uriValue] = useState(window.location.href);
  const [utmId] = useState(getutmId(window.location.search));
  const [campaignId] = useState(getCampaignId(window.location.search));
  const [location, setLocation] = useState({
    latitude: null,
    longitude: null,
  });
  const arranger_id_lead = localStorage.getItem("arranger_id_lead")
    ? Number(localStorage.getItem("arranger_id_lead"))
    : 0;
  const [peopleDataInfo, setPeopleDataInfo] = useState(null);
  const [flag, setFlag] = useState(false);

  const isViaOffline = window.location.search.includes("offline");

  useEffect(() => {
    if (!flag) {
      fetchData();
      setFlag(true);
    }
  }, []);

  const expireTimeout = Date.now() + 24 * 60 * 60 * 1000; // 24 hours
  //const expireTimeout = Date.now() + 10 * 1000; // 10 sec
  //const expireTimeout = Date.now() + 10 * 60 * 1000; // 10 mins

  const oldLocalStorageSession = localStorage.getItem("guest-fly-session");
  if (oldLocalStorageSession) {
    localStorage.removeItem("guest-fly-session");
  }

  const fetchData = async () => {
    const peopleDataValue = await getStorageValue("login_people_detail");
    setPeopleDataInfo(peopleDataValue || null);
  };

  useEffect(() => {
    if (!flag) {
      fetchData();
      setFlag(true);
    }
  }, []);

  const getCurrentTimeInUTC630 = () => {
    return moment().utcOffset(390).format("YYYY-MM-DD HH:mm:ss");
  };

  const checkLocalStorageSupport = () => {
    let localstorageSupport;
    try {
      if (typeof localStorage !== "undefined") {
        // Test writing to localStorage
        localStorage.setItem("test", "testValue");
        // Test reading from localStorage
        const value = localStorage.getItem("test");
        // Test removing from localStorage
        localStorage.removeItem("test");

        localstorageSupport =
          value === "testValue"
            ? "localStorage is working"
            : "localStorage is not working";
      } else {
        throw new Error("localStorage is not supported");
      }
    } catch (e) {
      localstorageSupport = "localStorage is not supported";
    }
    return localstorageSupport;
  };

  const checkIndexedDBSuppport = () => {
    return new Promise((resolve) => {
      if (!window.indexedDB) {
        resolve("IndexedDB is not supported");
        return;
      }

      // Try to open a test database
      const request = indexedDB.open("testDatabase");

      request.onsuccess = (event) => {
        const db = event.target.result;
        db.close();
        indexedDB.deleteDatabase("testDatabase");
        resolve("IndexedDB is working");
      };

      request.onerror = (event) => {
        resolve("IndexedDB is not working");
      };
    });
  };

  const handleStorageCheck = async () => {
    const localStorageResult = checkLocalStorageSupport();
    const indexedDBResult = await checkIndexedDBSuppport();
    return {
      localstorage_support: localStorageResult,
      indexeddb_support: indexedDBResult,
    };
  };

  const updateLocalStorageSession = (id, sessionId) => {
    const guestSession = localStorage.getItem(localStorageName);
    const arrangerData =
      isAuthenticated() && localStorage.getItem(LOGIN_PEOPLE_DETAIL);
    const arranger =
      arrangerData && arrangerData !== "" ? JSON.parse(arrangerData) : null;
    if (guestSession) {
      localStorage.setItem(
        localStorageName,
        JSON.stringify({
          sessionId,
          expireTimeout,
          id: id,
          arrangerId: isAuthenticated()
            ? arranger && arranger.id
              ? arranger.id
              : null
            : 0,
          updatedAt: getCurrentTimeInUTC630(),
        }),
      );
    }
  };

  const checkDeviceIdAndRegenerate = async () => {
    const localDeviceUuId = localStorage.getItem("device_id_uuidv4");
    const hasDeviceId =
      localDeviceUuId &&
      (localDeviceUuId !== "" ||
        localDeviceUuId !== null ||
        localDeviceUuId !== "null" ||
        localDeviceUuId !== undefined ||
        localDeviceUuId !== "undefined")
        ? true
        : false;

    //--- if device_id_uuidv4 not exits, regenerate device id and add to local storage  ----
    if (!hasDeviceId) {
      await setDeviceIdsToLocalStorage();
    }
  };

  const [createSession] = useMutation(CREATE_SESSION, {
    onCompleted: (res) => {
      updateLocalStorageSession(
        Number(res?.createFlymyaSession?.id),
        res?.createFlymyaSession?.session_id,
      );
      const startTime = Date.now();

      // Simulate logging the API response size
      const responseSize =
        new TextEncoder().encode(JSON.stringify(res)).length / 1024;

      // Log details to console
      const duration = Date.now() - startTime;
      const endpoint = "createFlymyaSession"; // The name of the mutation endpoint

      // Save to IndexedDB
      //saveApiLog(endpoint, duration, responseSize.toFixed(2));
    },
    onError: (err) => {
      console.log(err);
    },
  });

  const [updateSession] = useMutation(CREATE_SESSION, {
    onCompleted: (res) => {
      const startTime = Date.now();

      // Simulate logging the API response size
      const responseSize =
        new TextEncoder().encode(JSON.stringify(res)).length / 1024;

      // Log details to console
      const duration = Date.now() - startTime;
      const endpoint = "createFlymyaSession"; // The name of the mutation endpoint

      // Save to IndexedDB
      //saveApiLog(endpoint, duration, responseSize.toFixed(2));
    },
    onError: (err) => {
      console.log(err);
    },
  });

  const generateSessionId = () => {
    return uuidv4();
  };

  const generateDeviceId = () => {
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substr(2, 6);
    return timestamp + randomStr;
  };

  const generateSecureDeviceId = useCallback(() => {
    const crypto = window.crypto || window.msCrypto;
    if (crypto && crypto.getRandomValues) {
      const array = new Uint32Array(2);
      crypto.getRandomValues(array);
      return array[0].toString(36) + array[1].toString(36);
    } else {
      return generateDeviceId();
    }
  }, []);

  const setDeviceIdsToLocalStorage = useCallback(
    async (cusEmail, cusPhone) => {
      const deviceIds = localStorage.getItem(deviceIdsName);

      const cookieData = Cookies.get("AMP_9bdc728a74");
      const cookie =
        cookieData && cookieData !== "" && isJson(cookieData)
          ? JSON.parse(cookieData)
          : null;
      const cookieDataTwo = getCookie("AMP_9bdc728a74");
      const cookieTwo =
        cookieDataTwo && cookieDataTwo !== "" && isJson(cookieDataTwo)
          ? JSON.parse(cookieDataTwo)
          : null;

      const emailVal = localStorage.getItem("identifier");
      const loginDetail = localStorage.getItem("login_people_detail");
      const loginDetailData =
        loginDetail && isJson(loginDetail) ? JSON.parse(loginDetail) : null;

      const emailSessionData =
        emailVal && emailVal !== ""
          ? emailVal
          : loginDetailData
          ? loginDetailData.arranger_emails &&
            loginDetailData.arranger_emails.length > 0 &&
            loginDetailData.arranger_emails.find((val) => val.is_primary)
            ? loginDetailData.arranger_emails.find((val) => val.is_primary)
                .email
            : cusEmail
            ? cusEmail
            : "-"
          : cusEmail
          ? cusEmail
          : "-";

      const phoneSessionData = loginDetailData
        ? loginDetailData.arranger_phones &&
          loginDetailData.arranger_phones.length > 0 &&
          loginDetailData.arranger_phones.find((val) => val.is_primary)
          ? `${
              loginDetailData.arranger_phones.find((val) => val.is_primary)
                .phone_prefix
            } ${
              loginDetailData.arranger_phones.find((val) => val.is_primary)
                .phone
            }`
          : cusPhone
          ? cusPhone
          : "-"
        : cusPhone
        ? cusPhone
        : "-";

      const existingUUID = localStorage.getItem("device_id_uuidv4");
      // const newUUID = `${generateSecureDeviceId()}-${generateSessionId()}`;

      let newUUID = "";
      const sessionId = generateSessionId();
      const secureDeviceId = generateSecureDeviceId();
      if (sessionId && secureDeviceId) {
        newUUID = `${secureDeviceId}-${sessionId}`;
      } else {
        newUUID = sessionId ? sessionId : secureDeviceId;
      }

      const allItems = await getItems();
      if (allItems.length === 0) {
        await addItem({
          device_id_uuidv4:
            existingUUID &&
            (existingUUID !== "" ||
              existingUUID !== null ||
              existingUUID !== "null" ||
              existingUUID !== undefined ||
              existingUUID !== "undefined")
              ? existingUUID
              : newUUID,
        });
      }

      if (!deviceIds || deviceIds === "" || deviceIds === undefined) {
        localStorage.setItem(
          deviceIdsName,
          JSON.stringify({
            device_id_react:
              cookie && cookie.deviceId
                ? cookie.deviceId
                : cookieTwo && cookieTwo.deviceId
                ? cookieTwo.deviceId
                : "-",
            device_id_uuidv4:
              existingUUID &&
              (existingUUID !== "" ||
                existingUUID !== null ||
                existingUUID !== "null" ||
                existingUUID !== undefined ||
                existingUUID !== "undefined")
                ? existingUUID
                : newUUID,
            device_id_email: emailSessionData,
            device_id_full_phone: phoneSessionData,
          }),
        );
        window.location.search.includes("miniapp") && window?.ma
          ? window?.ma.setStorage({
              key: "device_id_react",
              data:
                cookie && cookie.deviceId
                  ? cookie.deviceId
                  : cookieTwo && cookieTwo.deviceId
                  ? cookieTwo.deviceId
                  : "-",
            })
          : localStorage.setItem(
              "device_id_react",
              cookie && cookie.deviceId
                ? cookie.deviceId
                : cookieTwo && cookieTwo.deviceId
                ? cookieTwo.deviceId
                : "-",
            );
        window.location.search.includes("miniapp") && window?.ma
          ? window.ma.setStorage({
              key: "device_id_uuidv4",
              data:
                existingUUID &&
                (existingUUID !== "" ||
                  existingUUID !== null ||
                  existingUUID !== "null" ||
                  existingUUID !== undefined ||
                  existingUUID !== "undefined")
                  ? existingUUID
                  : newUUID,
            })
          : localStorage.setItem(
              "device_id_uuidv4",
              existingUUID &&
                (existingUUID !== "" ||
                  existingUUID !== null ||
                  existingUUID !== "null" ||
                  existingUUID !== undefined ||
                  existingUUID !== "undefined")
                ? existingUUID
                : newUUID,
            );
        window.location.search.includes("miniapp") && window?.ma
          ? window.ma.setStorage({
              key: "device_id_email",
              data: emailSessionData,
            })
          : localStorage.setItem("device_id_email", emailSessionData);
        window.location.search.includes("miniapp") && window?.ma
          ? window.ma.setStorage({
              key: "device_id_full_phone",
              data: phoneSessionData,
            })
          : localStorage.setItem("device_id_full_phone", phoneSessionData);
        localStorage.setItem(
          "device_id_react",
          cookie && cookie.deviceId
            ? cookie.deviceId
            : cookieTwo && cookieTwo.deviceId
            ? cookieTwo.deviceId
            : "-",
        );
        localStorage.setItem(
          "device_id_uuidv4",
          existingUUID &&
            (existingUUID !== "" ||
              existingUUID !== null ||
              existingUUID !== "null" ||
              existingUUID !== undefined ||
              existingUUID !== "undefined")
            ? existingUUID
            : newUUID,
        );
        localStorage.setItem("device_id_email", emailSessionData);
        localStorage.setItem("device_id_full_phone", phoneSessionData);

        return;
      }

      const deviceIdsData =
        deviceIds && isJson(deviceIds) ? JSON.parse(deviceIds) : null;

      localStorage.setItem(
        deviceIdsName,
        JSON.stringify({
          device_id_react:
            cookie && cookie.deviceId
              ? cookie.deviceId
              : cookieTwo && cookieTwo.deviceId
              ? cookieTwo.deviceId
              : "-",
          device_id_uuidv4: deviceIdsData.device_id_uuidv4,
          device_id_email:
            deviceIdsData.device_id_email !== "-"
              ? deviceIdsData.device_id_email
              : emailSessionData,
          device_id_full_phone:
            deviceIdsData.device_id_full_phone !== "-"
              ? deviceIdsData.device_id_full_phone
              : phoneSessionData,
        }),
      );
      localStorage.setItem(
        "device_id_react",
        cookie && cookie.deviceId
          ? cookie.deviceId
          : cookieTwo && cookieTwo.deviceId
          ? cookieTwo.deviceId
          : "-",
      );
      localStorage.setItem("device_id_uuidv4", deviceIdsData.device_id_uuidv4);
      localStorage.setItem(
        "device_id_email",
        deviceIdsData.device_id_email !== "-"
          ? deviceIdsData.device_id_email
          : emailSessionData,
      );
      localStorage.setItem(
        "device_id_full_phone",
        deviceIdsData.device_id_full_phone !== "-"
          ? deviceIdsData.device_id_full_phone
          : phoneSessionData,
      );
    },
    [generateSecureDeviceId],
  );

  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  };

  const isAndroid = () => {
    return /Android/i.test(navigator.userAgent);
  };

  const createNewSession = useCallback(async () => {
    await checkDeviceIdAndRegenerate();
    const sessionId = generateSessionId();
    const hasGuestSession = localStorage.getItem(localStorageName);
    const updatedAt = hasGuestSession
      ? JSON.parse(hasGuestSession).updatedAt
      : null;
    const arrangerData =
      isAuthenticated() && localStorage.getItem(LOGIN_PEOPLE_DETAIL);
    const arranger =
      arrangerData && arrangerData !== "" && isJson(arrangerData)
        ? JSON.parse(arrangerData)
        : null;

    const clientTag = getClientTag(window.location.search);

    localStorage.setItem(
      localStorageName,
      JSON.stringify({
        sessionId,
        expirationTime: expireTimeout,
        arrangerId: isAuthenticated()
          ? arranger && arranger.id
            ? arranger.id
            : null
          : 0,
        updatedAt: updatedAt ? updatedAt : getCurrentTimeInUTC630(),
      }),
    );

    //const deviceIds = localStorage.getItem(deviceIdsName);
    const deviceUUID = localStorage.getItem("device_id_uuidv4");
    // const deviceIdsData =
    //   deviceIds && deviceIds !== "" && isJson(deviceIds)
    //     ? JSON.parse(deviceIds)
    //     : null;
    const allItems = await getItems();
    const deviceVal =
      allItems && allItems?.length > 0 && allItems?.[0]?.device_id_uuidv4
        ? allItems?.[0]?.device_id_uuidv4
        : deviceUUID;

    const { localstorage_support, indexeddb_support } =
      await handleStorageCheck();

    if (!hasGuestSession || (hasGuestSession && isGuestSessionExpired())) {
      const width = window.innerWidth;
      let viewType;

      if (width <= 767) viewType = "mobile";
      else if (width <= 1024) viewType = "tablet";
      else viewType = "desktop";

      createSession({
        variables: {
          input: {
            session_id: sessionId,
            arranger_id: isAuthenticated()
              ? arranger && arranger.id
                ? arranger.id
                : null
              : arranger_id_lead
              ? arranger_id_lead
              : 0,
            device_id:
              (isIOS() || isAndroid()) && deviceVal ? deviceVal : deviceUUID,
            client_platform_id:
              clientTag.replace(/\s/g, "") === "" ||
              clientTag === null ||
              clientTag === "null" ||
              clientTag === " " ||
              clientTag === undefined ||
              !clientTag
                ? "default"
                : clientTag,
            metadata: JSON.stringify({
              userAgent: navigator.userAgent,
              geoLocation: location,
              width,
              height: window.innerHeight,
              viewType,
            }),
            localstorage_support: localstorage_support,
            indexeddb_support: indexeddb_support,
            utm_id: utmId !== "" ? Number(utmId) : null,
            campaign_id: campaignId !== "" ? Number(campaignId) : null,
          },
        },
      });
    }
  }, [createSession, expireTimeout]);

  const isGuestSessionExpired = () => {
    const guestSession = localStorage.getItem(localStorageName);

    if (
      guestSession &&
      guestSession !== "null" &&
      guestSession !== "undefined" &&
      isJson(guestSession)
    ) {
      const guestSessionData = JSON.parse(guestSession);
      const updatedAtMoment = moment(
        guestSessionData?.updatedAt,
        "YYYY-MM-DD HH:mm:ss",
      ).utcOffset(390);
      const now = moment().utcOffset(390);
      const differenceInMinutes = now.diff(updatedAtMoment, "minutes");
      const isSessionExpired = differenceInMinutes > 30;
      return isSessionExpired;
    }

    // if (guestSession) {
    //   const { expirationTime } = JSON.parse(guestSession);
    //   return expirationTime < Date.now();
    // }

    return false;
  };

  const handleInitDB = () => {
    const initDB = async () => {
      await openDB();
      //const allItems = await getItems();
      //console.log("all", allItems);

      // if (allItems.length === 0) {
      //   handleAddUUIDToDB();
      // }
    };

    initDB();
  };

  // const handleAddUUIDToDB = async () => {
  //   await addItem({
  //     device_id_uuidv4: `${generateSecureDeviceId()}-${generateSessionId()}`,
  //   });
  // };

  // const handleUpdateItem = async (id, newVal) => {
  //   await updateItem({ id, name: newVal });
  // };

  // const handleDeleteItem = async (id) => {
  //   await deleteItem(id);
  // };

  const getGeoLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          //console.error("Error getting location:", error.message);
          setLocation({
            latitude: null,
            longitude: null,
            errMessage: error.message,
          });
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 },
      );
    } else {
      //console.error("Geolocation is not supported by this browser.");
      setLocation({
        latitude: null,
        longitude: null,
        errMessage: "Geolocation is not supported by this browser.",
      });
    }
  };

  const handleFlymyaSession = () => {
    localStorage.removeItem("guestSession");
    localStorage.removeItem("guest-session");

    handleInitDB();
    setDeviceIdsToLocalStorage();
    getGeoLocation();

    const guestSession = localStorage.getItem(localStorageName);

    if (!guestSession) {
      createNewSession();
      return;
    }
    const guestSessionData =
      guestSession !== "null" &&
      guestSession !== "undefined" &&
      isJson(guestSession) &&
      JSON.parse(guestSession);
    const arrangerData =
      isAuthenticated() && localStorage.getItem(LOGIN_PEOPLE_DETAIL);
    const arranger =
      arrangerData && arrangerData !== "" ? JSON.parse(arrangerData) : null;

    // if (
    //   guestSession &&
    //   !isGuestSessionExpired() &&
    //   (arrangerId === null || arrangerId === "null")
    // ) {
    //   createSession({
    //     variables: {
    //       input: {
    //         id: Number(id),
    //         session_id: sessionId,
    //         arranger_id: isAuthenticated()
    //           ? arranger && arranger.id
    //             ? arranger.id
    //             : null
    //           : 0,
    //       },
    //     },
    //   });
    //   return;
    // }
    if (guestSession && isGuestSessionExpired()) {
      localStorage.setItem("arranger_id_lead", 0);
      const updatedAtMoment = moment(
        guestSessionData?.updatedAt,
        "YYYY-MM-DD HH:mm:ss",
      ).utcOffset(390);
      const newUpdatedAtMoment = updatedAtMoment.add(30, "minutes");
      //update the session with expired_at = updated_at+30 min
      guestSessionData &&
        guestSessionData.id &&
        updateSession({
          variables: {
            input: {
              id: Number(guestSessionData?.id),
              session_id: guestSessionData?.sessionId,
              // expired_at: moment(expirationTime).format("YYYY-MM-DD HH:mm:ss"),
              expired_at: newUpdatedAtMoment.format("YYYY-MM-DD HH:mm:ss"),
            },
          },
        });
      createNewSession();
    }
  };

  const [createUserAction] = useMutation(CREATE_USER_ACTION, {
    onCompleted: async (res) => {
      localStorage.setItem("userActionId", res?.createUserAction?.id);
      const startTime = Date.now();

      // Simulate logging the API response size
      const responseSize =
        new TextEncoder().encode(JSON.stringify(res)).length / 1024;

      // Log details to console
      const duration = Date.now() - startTime;
      const endpoint = "createUserAction"; // The name of the mutation endpoint

      // Save to IndexedDB
      //saveApiLog(endpoint, duration, responseSize.toFixed(2));

      //--- update session every time a userAction is created ----

      const guestSession = localStorage.getItem(localStorageName);
      const arrangerData =
        isAuthenticated() && localStorage.getItem(LOGIN_PEOPLE_DETAIL);
      const arranger =
        arrangerData && arrangerData !== "" ? JSON.parse(arrangerData) : null;

      const deviceId = localStorage.getItem("device_id_uuidv4");

      //const arrId = res?.createUserAction?.arranger?.id || 0;

      if (
        guestSession &&
        guestSession !== "null" &&
        guestSession !== "undefined" &&
        isJson(guestSession)
      ) {
        const guestData = JSON.parse(guestSession);
        const class_types_name = isAuthenticated()
          ? getClassTypeName()
          : "Guest";
        let input = {
          id: Number(guestData?.id),
          session_id: guestData?.sessionId,

          arranger_id: isAuthenticated()
            ? arranger && arranger.id
              ? arranger.id
              : 0
            : arranger_id_lead
            ? arranger_id_lead
            : 0,
          device_id: deviceId,
          ...(class_types_name && { class_types_name }),
        };
        if (isViaOffline) {
          input.via_url = "offline";
        }
        guestData &&
          guestData.id &&
          res?.createUserAction?.id &&
          updateSession({
            variables: {
              input: input,
            },
          });
        updateLocalStorageSession(Number(guestData?.id), guestData?.sessionId);
      }
      //-----
    },
    onError: (err) => {
      console.log(err);
    },
  });

  const handleCreateUserAction = ({ eventName, serializedPayload }) => {
    const guestSession = localStorage.getItem(localStorageName);

    const arrangerData =
      isAuthenticated() && localStorage.getItem(LOGIN_PEOPLE_DETAIL);

    const arranger =
      arrangerData && arrangerData !== "" && isJson(arrangerData)
        ? JSON.parse(arrangerData)
        : null;

    if (
      guestSession &&
      isJson(guestSession) &&
      guestSession !== "null" &&
      guestSession !== "undefined"
    ) {
      const guestData = JSON.parse(guestSession);
      checkDeviceIdAndRegenerate();
      const payloadObject = serializedPayload
        ? JSON.parse(serializedPayload)
        : null;
      let updatedSerializedPayload = payloadObject;
      if (payloadObject?.logged_in_status === undefined) {
        updatedSerializedPayload = {
          ...payloadObject,
          logged_in_status: isAuthenticated() ? "true" : "false",
        };
      }
      // Like logged_in_status: the stored KBZPay phone and nickname go on
      // every event that does not set its own.
      const kbzUserFields = getKbzUserFields();
      if (Object.keys(kbzUserFields).length > 0) {
        updatedSerializedPayload = {
          ...kbzUserFields,
          ...updatedSerializedPayload,
        };
      }
      if (updatedSerializedPayload && eventName?.startsWith("hotel_short_")) {
        const { flights: _flights, ...hotelOnlyPayload } =
          updatedSerializedPayload;
        updatedSerializedPayload = hotelOnlyPayload;
      } else if (updatedSerializedPayload) {
        const { hotels: _hotels, ...nonHotelPayload } =
          updatedSerializedPayload;
        updatedSerializedPayload = nonHotelPayload;
        if (updatedSerializedPayload.reservation_id !== undefined) {
          delete updatedSerializedPayload.reservation_id;
        }
      }
      // Returned so a caller that is about to leave (e.g. the KBZPay reject,
      // which clears storage and exits) can wait for the event to go out.
      return createUserAction({
        variables: {
          input: {
            flymya_event: eventName,
            flymya_session: guestData?.sessionId,
            utm_id: utmId !== "" ? Number(utmId) : null,
            campaign: campaignId !== "" ? campaignId : null,
            uri: uriValue,
            serialized_payload: updatedSerializedPayload
              ? JSON.stringify(updatedSerializedPayload)
              : serializedPayload,
            arranger_id: isAuthenticated()
              ? arranger && arranger.id
                ? arranger.id
                : 0
              : payloadObject && payloadObject.arranger_id
              ? payloadObject.arranger_id
              : arranger_id_lead
              ? arranger_id_lead
              : 0,
          },
        },
      });
    }
  };

  const handleUpdateArrangerId = async (arrangerIdVal) => {
    await checkDeviceIdAndRegenerate();
    const guestSession = localStorage.getItem(localStorageName);
    const { localstorage_support, indexeddb_support } =
      await handleStorageCheck();
    if (
      guestSession &&
      guestSession !== "null" &&
      guestSession !== "undefined" &&
      isJson(guestSession)
    ) {
      const guestData = JSON.parse(guestSession);
      if (
        guestSession &&
        !isGuestSessionExpired() &&
        (guestData?.arrangerId === null ||
          guestData?.arrangerId === "null" ||
          guestData?.arrangerId === "")
      ) {
        guestData &&
          guestData.id &&
          createSession({
            variables: {
              input: {
                id: Number(guestData?.id),
                session_id: guestData?.sessionId,
                arranger_id: arrangerIdVal ? arrangerIdVal : null,
                localstorage_support: localstorage_support,
                indexeddb_support: indexeddb_support,
              },
            },
          });
      }
    }
  };

  // Only the fields that change what a logged-in customer is shown. The login
  // mutations and GET_LOGIN_PEOPLE_DETAILS select different partnership fields
  // (the details query also asks for `name`) and neither guarantees an order,
  // so comparing the raw payloads reported "changed" on every single call.
  const partnershipPricingKey = (partnerships) =>
    JSON.stringify(
      (Array.isArray(partnerships) ? [...partnerships] : [])
        .map((p) => ({
          id: Number(p?.id),
          class_type_id: p?.class_type_id ?? null,
          show_dual_pricing: p?.show_dual_pricing ?? null,
        }))
        .sort((a, b) => a.id - b.id),
    );

  const getLoginPeopleDetail = () => {
    // Coalesce a burst into the single request it was meant to be. Without
    // this, one caller re-firing on every render puts hundreds of concurrent
    // requests on the wire, and the browser rejects the overflow with
    // ERR_INSUFFICIENT_RESOURCES.
    if (loginPeopleDetailInFlight) return loginPeopleDetailInFlight;
    if (
      loginPeopleDetailLastFetchedAt &&
      Date.now() - loginPeopleDetailLastFetchedAt <
        LOGIN_PEOPLE_DETAIL_COOLDOWN_MS
    ) {
      return Promise.resolve();
    }

    loginPeopleDetailInFlight = clientV3()
      .query({
        query: GET_LOGIN_PEOPLE_DETAILS,
        fetchPolicy: "network-only",
        variables: {},
      })
      .then((res) => {
        const partnerships = res?.data?.loginPeopleDetails?.partnerships;
        if (
          Number(res?.data?.loginPeopleDetails?.id) !==
            Number(localStorage.getItem(LOGIN_USER_PEOPLE)) ||
          !(partnerships?.length > 0)
        ) {
          return;
        }

        // Read through rather than using the render-time snapshot, which is
        // stale by the time this response lands.
        const storedRaw = localStorage.getItem(ARR_PARTNERSHIP_OBJ);
        const stored =
          storedRaw && isJson(storedRaw) ? JSON.parse(storedRaw) : null;
        const freshRaw = JSON.stringify(partnerships);
        if (storedRaw === freshRaw) return;

        // Keep storage current either way, so consumers that read fields the
        // login mutations never stored (`name`) pick them up on the next render.
        localStorage.setItem(ARR_PARTNERSHIP_OBJ, freshRaw);

        // A reload is only warranted when pricing-relevant data actually moved -
        // otherwise every visit reloaded, and each reload re-ran this query and
        // reloaded again, hammering /graphql/customer until the browser ran out
        // of sockets (ERR_INSUFFICIENT_RESOURCES).
        if (
          partnershipPricingKey(stored) === partnershipPricingKey(partnerships)
        ) {
          return;
        }

        // Belt and braces: even if the data genuinely disagrees on every call,
        // this can fire at most once per tab session instead of looping.
        if (sessionStorage.getItem(PARTNERSHIP_RELOAD_FLAG)) return;
        sessionStorage.setItem(PARTNERSHIP_RELOAD_FLAG, "1");
        window.location.reload();
      })
      .catch((err) => {})
      .finally(() => {
        loginPeopleDetailLastFetchedAt = Date.now();
        loginPeopleDetailInFlight = null;
      });

    return loginPeopleDetailInFlight;
  };

  return {
    createNewSession,
    handleFlymyaSession,
    handleCreateUserAction,
    setDeviceIdsToLocalStorage,
    handleUpdateArrangerId,
    getLoginPeopleDetail,
  };
}

export default FlymyaSessionHook;
